/**
 * API Configuration
 * Central configuration for API endpoints and settings
 */

// Get API base URL from environment variable or use default
const getApiBaseUrl = (): string => {
  // In production, this should be set via environment variable
  // For local development, assumes backend runs on localhost:5001
  return process.env.REACT_APP_API_URL || 'https://localhost:5001';
};

export const API_BASE_URL = getApiBaseUrl();

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: `${API_BASE_URL}/api/auth/login`,
    REGISTER: `${API_BASE_URL}/api/auth/register`,
    REGISTER_USER: `${API_BASE_URL}/api/auth/register/user`,
    REGISTER_COMPANY: `${API_BASE_URL}/api/auth/register/company`,
    REQUEST_ACCESS: `${API_BASE_URL}/api/auth/request-access`,
    LOGOUT: `${API_BASE_URL}/api/auth/logout`,
    REFRESH: `${API_BASE_URL}/api/auth/refresh`,
    ME: `${API_BASE_URL}/api/auth/me`,
    CHANGE_PASSWORD: `${API_BASE_URL}/api/auth/change-password`,
    // Email MFA endpoints (mandatory two-factor authentication)
    VERIFY_MFA: `${API_BASE_URL}/api/auth/verify-mfa`,
    RESEND_MFA: `${API_BASE_URL}/api/auth/resend-mfa`,
    // Password Reset
    FORGOT_PASSWORD: `${API_BASE_URL}/api/auth/forgot-password`,
    VALIDATE_RESET_TOKEN: (token: string) => `${API_BASE_URL}/api/auth/validate-reset-token/${token}`,
    RESET_PASSWORD: `${API_BASE_URL}/api/auth/reset-password`,
    // Email Verification
    VERIFY_EMAIL: (token: string) => `${API_BASE_URL}/api/auth/verify-email/${token}`,
    RESEND_VERIFICATION: `${API_BASE_URL}/api/auth/resend-verification`,
  },
  USERS: {
    ME: `${API_BASE_URL}/api/users/me`,
    UPDATE_PROFILE: `${API_BASE_URL}/api/users/me`,
    POLICIES: `${API_BASE_URL}/api/users/me/policies`,
    TOUR_STATUS: `${API_BASE_URL}/api/users/me/tour-status`,
    TOUR_COMPLETED: `${API_BASE_URL}/api/users/me/tour-completed`,
  },
  CLIENTS: {
    GET: (clientId: string) => `${API_BASE_URL}/api/clients/${clientId}`,
    UPDATE_POLICIES: (clientId: string) => `${API_BASE_URL}/api/clients/${clientId}/policies`,
    LIST_USERS: (clientId: string) => `${API_BASE_URL}/api/clients/${clientId}/users`,
    INVITE_USER: (clientId: string) => `${API_BASE_URL}/api/clients/${clientId}/users/invite`,
    MY_PRICING: `${API_BASE_URL}/api/clients/my/pricing`,
    MY_DPPA: `${API_BASE_URL}/api/clients/my-dppa`,
  },
  // Signed agreements held against a client. Reading is ClientAdmin (own org) or SystemAdmin;
  // uploading, replacing and removing are SystemAdmin only, enforced by the API.
  CLIENT_DOCUMENTS: {
    LIST: (clientId: string) => `${API_BASE_URL}/api/clients/${clientId}/documents`,
    UPLOAD_MSA: (clientId: string) => `${API_BASE_URL}/api/clients/${clientId}/documents/msa`,
    DOWNLOAD: (clientId: string, documentId: string) =>
      `${API_BASE_URL}/api/clients/${clientId}/documents/${documentId}/download`,
    REMOVE: (clientId: string, documentId: string) =>
      `${API_BASE_URL}/api/clients/${clientId}/documents/${documentId}`,
  },
  API_KEYS: {
    LIST: `${API_BASE_URL}/api/apikeys`,
    CREATE: `${API_BASE_URL}/api/apikeys`,
    REVOKE: (keyId: string) => `${API_BASE_URL}/api/apikeys/${keyId}`,
    ADMIN_LIST_ALL: `${API_BASE_URL}/api/apikeys/admin/all`,
    ADMIN_REVOKE: (keyId: string) => `${API_BASE_URL}/api/apikeys/admin/${keyId}`,
  },
  INVITATIONS: {
    LIST: `${API_BASE_URL}/api/invitations`,
    CREATE: `${API_BASE_URL}/api/invitations`,
    GET: (id: string) => `${API_BASE_URL}/api/invitations/${id}`,
    RESEND: (id: string) => `${API_BASE_URL}/api/invitations/${id}/resend`,
    CANCEL: (id: string) => `${API_BASE_URL}/api/invitations/${id}`,
    VALIDATE_TOKEN: (token: string) => `${API_BASE_URL}/api/invitations/accept/${token}`,
    ACCEPT_TOKEN: (token: string) => `${API_BASE_URL}/api/invitations/accept/${token}`,
  },
  USER_MANAGEMENT: {
    UPDATE_STATUS: (userId: string) => `${API_BASE_URL}/api/users/${userId}/status`,
    UPDATE_ROLE: (userId: string) => `${API_BASE_URL}/api/users/${userId}/role`,
    DELETE: (userId: string) => `${API_BASE_URL}/api/users/${userId}/permanent`,
    RESTORE: (userId: string) => `${API_BASE_URL}/api/users/${userId}/restore`,
    FORCE_DELETE: (userId: string) => `${API_BASE_URL}/api/users/${userId}/force`,
    UPDATE_PERMISSIONS: (userId: string) => `${API_BASE_URL}/api/users/${userId}/permissions`,
  },
  USER_ACTIVITY: {
    LOGINS: (userId: string) => `${API_BASE_URL}/api/users/${userId}/activity/logins`,
    CHANGES: (userId: string) => `${API_BASE_URL}/api/users/${userId}/activity/changes`,
    API_USAGE: (userId: string) => `${API_BASE_URL}/api/users/${userId}/activity/api-usage`,
    SUMMARY: (userId: string) => `${API_BASE_URL}/api/users/${userId}/activity/summary`,
    ADMIN_ALL: `${API_BASE_URL}/api/users/admin/all`,
  },
  VEHICLE_REPORTS: {
    CREATE: `${API_BASE_URL}/api/vehiclereports`,
    LIST: `${API_BASE_URL}/api/vehiclereports`,
    GET: (reportId: string) => `${API_BASE_URL}/api/vehiclereports/${reportId}`,
    GENERATE_PDF: `${API_BASE_URL}/api/vehiclereports/generate-pdf`,
    SAMPLE_PDF: `${API_BASE_URL}/api/vehiclereports/sample-pdf`,
    GENERATE_PDF_FROM_LOG: `${API_BASE_URL}/api/vehiclereports/generate-pdf-from-log`,
    BULK_CREATE: `${API_BASE_URL}/api/vehiclereports/bulk`,
    GENERATE_BULK_PDF: `${API_BASE_URL}/api/vehiclereports/generate-bulk-pdf`,
    PRICING: `${API_BASE_URL}/api/vehiclereports/pricing`,
    BRAND_CHECKLIST: `${API_BASE_URL}/api/vehiclereports/brand-checklist`,
  },
  VIN: {
    VALIDATE: `${API_BASE_URL}/api/vin/validate`,
    VALIDATE_QUICK: `${API_BASE_URL}/api/vin/validate-quick`,
    DECODE_EXTENDED: `${API_BASE_URL}/api/vin/decode-extended`,
    DECODE_DATAONE: `${API_BASE_URL}/api/vin/decode-dataone`,
    GENERATE_PDF: `${API_BASE_URL}/api/vin/generate-pdf`,
    PRICING: `${API_BASE_URL}/api/vin/pricing`,
  },
  STATE_CONTACT_INFO: {
    GET_BY_CODES: `${API_BASE_URL}/api/statecontactinfo`,
    GET_BY_CODE: (stateCode: string) => `${API_BASE_URL}/api/statecontactinfo/${stateCode}`,
    GET_ALL: `${API_BASE_URL}/api/statecontactinfo/all`,
  },
  API_LOGS: {
    SEARCH_HISTORY: `${API_BASE_URL}/api/apilogs/search-history`,
    EXPORT_CSV: `${API_BASE_URL}/api/apilogs/export-csv`,
    GET_BY_ID: (logId: string) => `${API_BASE_URL}/api/apilogs/${logId}`,
    GET_MVR_BY_ID: (logId: string) => `${API_BASE_URL}/api/apilogs/${logId}/mvr`,
    DASHBOARD_STATS: `${API_BASE_URL}/api/apilogs/dashboard-stats`,
    REPORTS_BY_USER: `${API_BASE_URL}/api/apilogs/reports/by-user`,
    REPORTS_BY_TYPE: `${API_BASE_URL}/api/apilogs/reports/by-type`,
    REPORTS_BY_DAY: `${API_BASE_URL}/api/apilogs/reports/by-day`,
    REPORTS_BY_STATE: `${API_BASE_URL}/api/apilogs/reports/by-state`,
    REPORTS_BY_USER_CSV: `${API_BASE_URL}/api/apilogs/reports/by-user/csv`,
    REPORTS_BY_TYPE_CSV: `${API_BASE_URL}/api/apilogs/reports/by-type/csv`,
    REPORTS_BY_DAY_CSV: `${API_BASE_URL}/api/apilogs/reports/by-day/csv`,
    REPORTS_BY_STATE_CSV: `${API_BASE_URL}/api/apilogs/reports/by-state/csv`,
  },
  FEATURE_CARDS: {
    LIST: `${API_BASE_URL}/api/featurecards`,
  },
  NVS: {
    SEARCH: `${API_BASE_URL}/api/nvs/search`,
    REPORTS: `${API_BASE_URL}/api/nvs/reports`,
    GET_REPORT: (reportId: string) => `${API_BASE_URL}/api/nvs/reports/${reportId}`,
    GENERATE_PDF: `${API_BASE_URL}/api/nvs/generate-pdf`,
    PRICING: `${API_BASE_URL}/api/nvs/pricing`,
    BULK_SEARCH: `${API_BASE_URL}/api/nvs/bulk-search`,
    GENERATE_BULK_PDF: `${API_BASE_URL}/api/nvs/generate-bulk-pdf`,
  },
  SETTINGS: {
    PUBLIC: `${API_BASE_URL}/api/settings/public`,
    REGISTRATION: `${API_BASE_URL}/api/settings/registration`,
    DASHBOARD: `${API_BASE_URL}/api/settings/dashboard`,
    NAVIGATION: `${API_BASE_URL}/api/settings/navigation`,
    POLICY: (type: string) => `${API_BASE_URL}/api/settings/policy/${type}`,
  },
  NOTIFICATIONS: {
    LIST: `${API_BASE_URL}/api/notifications`,
    COUNT: `${API_BASE_URL}/api/notifications/count`,
    MARK_READ: (id: string) => `${API_BASE_URL}/api/notifications/${id}/read`,
    MARK_ALL_READ: `${API_BASE_URL}/api/notifications/read-all`,
    DISMISS: (id: string) => `${API_BASE_URL}/api/notifications/${id}/dismiss`,
    CREATE_SYSTEM: `${API_BASE_URL}/api/notifications/system`,
    CREATE_SYSTEM_ADMINS: `${API_BASE_URL}/api/notifications/system-admins`,
    CREATE_CLIENT: (clientId: string) => `${API_BASE_URL}/api/notifications/client/${clientId}`,
    CREATE_USER: (userId: string) => `${API_BASE_URL}/api/notifications/user/${userId}`,
    DEACTIVATE: (id: string) => `${API_BASE_URL}/api/notifications/${id}`,
    GET_CREATED: `${API_BASE_URL}/api/notifications/created`,
  },
  CONTACT: {
    SUBMIT: `${API_BASE_URL}/api/contact`,
  },
  IDAHO_P2P: {
    TITLE_SEARCH: `${API_BASE_URL}/api/idahop2p/title/search`,
    REGISTRATION_SEARCH: `${API_BASE_URL}/api/idahop2p/registration/search`,
    TEST_LOGIN: `${API_BASE_URL}/api/idahop2p/test-login`,
    GENERATE_PDF: `${API_BASE_URL}/api/idahop2p/generate-pdf`,
  },
  STATE_DMV: {
    STATES: `${API_BASE_URL}/api/state-dmv/states`,
    TITLE: (state: string) => `${API_BASE_URL}/api/state-dmv/${encodeURIComponent(state)}/title`,
    REGISTRATION: (state: string) => `${API_BASE_URL}/api/state-dmv/${encodeURIComponent(state)}/registration`,
    COMBINED: (state: string) => `${API_BASE_URL}/api/state-dmv/${encodeURIComponent(state)}/combined`,
    PRICING: `${API_BASE_URL}/api/state-dmv/pricing`,
    BULK_CREATE: `${API_BASE_URL}/api/state-dmv/bulk`,
    BULK_PRICING: `${API_BASE_URL}/api/state-dmv/bulk-pricing`,
    BULK_MVR_PDF: `${API_BASE_URL}/api/state-dmv/bulk-mvr-pdf`,
    MVR_PDF: (state: string) => `${API_BASE_URL}/api/state-dmv/${encodeURIComponent(state)}/mvr-pdf`,
    SAMPLE_MVR_PDF: `${API_BASE_URL}/api/state-dmv/sample-mvr-pdf`,
  },
  ASTS_PUBLIC: {
    STATES: `${API_BASE_URL}/api/asts-public/states`,
    SEARCH: (state: string) => `${API_BASE_URL}/api/asts-public/${encodeURIComponent(state)}/search`,
    BULK: `${API_BASE_URL}/api/asts-public/bulk`,
    BULK_PDF: `${API_BASE_URL}/api/asts-public/bulk-pdf`,
    PDF: (state: string) => `${API_BASE_URL}/api/asts-public/${encodeURIComponent(state)}/pdf`,
  },
  COVERAGE: {
    STATES: `${API_BASE_URL}/api/coverage/states`,
  },
  SERVICE_PRICINGS: {
    LIST: `${API_BASE_URL}/api/service-pricings`,
    CREATE: `${API_BASE_URL}/api/service-pricings`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/service-pricings/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/api/service-pricings/${id}`,
  },
  SERVICE_PRICING_TIERS: {
    LIST: `${API_BASE_URL}/api/service-pricing-tiers`,
    SAVE: `${API_BASE_URL}/api/service-pricing-tiers`,
  },
  MARGINS: {
    NMVTIS: `${API_BASE_URL}/api/margins/nmvtis`,
    MVR: `${API_BASE_URL}/api/margins/mvr`,
    STATE_FEE: (pricingId: string) => `${API_BASE_URL}/api/margins/state-fees/${pricingId}`,
    AAMVA_SCHEDULE: `${API_BASE_URL}/api/margins/aamva-schedule`,
  },
  CLIENT_DISCOUNTS: {
    LIST: `${API_BASE_URL}/api/client-discounts`,
    CLIENTS: `${API_BASE_URL}/api/client-discounts/clients`,
    CREATE: `${API_BASE_URL}/api/client-discounts`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/client-discounts/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/api/client-discounts/${id}`,
  },
  ADMIN_SETTINGS: {
    LIST: `${API_BASE_URL}/api/admin/settings`,
    GET: (id: string) => `${API_BASE_URL}/api/admin/settings/${id}`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/admin/settings/${id}`,
    CREATE: `${API_BASE_URL}/api/admin/settings`,
  },
  ADMIN_REPORTS: {
    GENERATE_NMVTIS_QUARTERLY: `${API_BASE_URL}/api/admin/reports/nmvtis-consumer-access-quarterly/generate`,
  },
  JURISDICTIONS: {
    LIST: `${API_BASE_URL}/api/jurisdictions`,
    GET: (code: string) => `${API_BASE_URL}/api/jurisdictions/${code}`,
    UPDATE: (code: string) => `${API_BASE_URL}/api/jurisdictions/${code}`,
    REPORT_SECTIONS: (code: string) => `${API_BASE_URL}/api/jurisdictions/${code}/report-sections`,
    REPORT_FIELDS: (code: string) => `${API_BASE_URL}/api/jurisdictions/${code}/report-fields`,
  },
  CLIENTS_ADMIN: {
    LIST: `${API_BASE_URL}/api/clients/admin/list`,
    GET: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}`,
    PENDING: `${API_BASE_URL}/api/clients/admin/pending`,
    CREATE_PENDING: `${API_BASE_URL}/api/clients/admin/create-pending`,
    SETUP_ACTIVATE: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}/setup-activate`,
    REJECT: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}/reject`,
    USERS: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}/users`,
    INVITE_USER: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}/invite`,
    USER_TOGGLE_ACTIVE: (userId: string) => `${API_BASE_URL}/api/clients/admin/users/${userId}/toggle-active`,
    USER_REMOVE: (userId: string) => `${API_BASE_URL}/api/clients/admin/users/${userId}/remove`,
    USER_ROLE: (userId: string) => `${API_BASE_URL}/api/clients/admin/users/${userId}/role`,
    USER_INVOICING_ACCESS: (userId: string) => `${API_BASE_URL}/api/clients/admin/users/${userId}/invoicing-access`,
    USER_CLIENT_ADMIN_ACCESS: (userId: string) => `${API_BASE_URL}/api/clients/admin/users/${userId}/client-admin-access`,
    API_KEYS: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}/api-keys`,
    API_KEY_CREATE: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}/api-keys`,
    API_KEY_REVOKE: (keyId: string) => `${API_BASE_URL}/api/clients/admin/api-keys/${keyId}`,
    STATE_PRICES: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}/state-prices`,
    SERVICE_PRICES: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}/service-prices`,
    PRICING_TIERS: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}/pricing-tiers`,
    INVITATIONS: (id: string) => `${API_BASE_URL}/api/clients/admin/${id}/invitations`,
    INVITATION_LINK: (invitationId: string) => `${API_BASE_URL}/api/clients/admin/invitations/${invitationId}/link`,
    INVITATION_RESEND: (invitationId: string) => `${API_BASE_URL}/api/clients/admin/invitations/${invitationId}/resend`,
  },
  ADMIN_PORTAL_CREDENTIALS: {
    LIST: `${API_BASE_URL}/api/admin/portal-credentials`,
    UPDATE: (stateCode: string) => `${API_BASE_URL}/api/admin/portal-credentials/${encodeURIComponent(stateCode)}`,
    UPDATE_EXPIRY: (stateCode: string) => `${API_BASE_URL}/api/admin/portal-credentials/${encodeURIComponent(stateCode)}/expiry`,
  },
  ADMIN_NC_ROTATION: {
    STATUS: `${API_BASE_URL}/api/admin/nc-rotation`,
    ENABLED: `${API_BASE_URL}/api/admin/nc-rotation/enabled`,
    ROTATE_NOW: `${API_BASE_URL}/api/admin/nc-rotation/rotate-now`,
    CLEAR_HOLD: `${API_BASE_URL}/api/admin/nc-rotation/clear-hold`,
    RESOLVE: `${API_BASE_URL}/api/admin/nc-rotation/resolve`,
  },
  ADMIN_PORTAL_HEALTH: {
    CHECK: (stateCode: string) => `${API_BASE_URL}/api/admin/portal-health/${encodeURIComponent(stateCode)}`,
  },
  PROBLEM_REPORTS: {
    CREATE: `${API_BASE_URL}/api/problemreports`,
    MY_REPORTS: `${API_BASE_URL}/api/problemreports/my-reports`,
    GET: (id: string) => `${API_BASE_URL}/api/problemreports/${id}`,
    // Admin endpoints
    ADMIN_LIST: `${API_BASE_URL}/api/admin/problemreports`,
    ADMIN_GET: (id: string) => `${API_BASE_URL}/api/admin/problemreports/${id}`,
    ADMIN_OPEN_COUNT: `${API_BASE_URL}/api/admin/problemreports/open-count`,
    ADMIN_UPDATE_STATUS: (id: string) => `${API_BASE_URL}/api/admin/problemreports/${id}/status`,
    ADMIN_ASSIGN: (id: string) => `${API_BASE_URL}/api/admin/problemreports/${id}/assign`,
    ADMIN_UPDATE_NOTES: (id: string) => `${API_BASE_URL}/api/admin/problemreports/${id}/notes`,
  },
  INVOICES: {
    LIST: `${API_BASE_URL}/api/invoices`,
    CREATE: `${API_BASE_URL}/api/invoices`,
    RUNNING_TOTALS: `${API_BASE_URL}/api/invoices/running-totals`,
    MONTHLY_ACTIVITY: (netSuiteCustomerId: string, month: string) => `${API_BASE_URL}/api/invoices/monthly-activity?netSuiteCustomerId=${encodeURIComponent(netSuiteCustomerId)}&month=${encodeURIComponent(month)}`,
    APPROVE: (id: string) => `${API_BASE_URL}/api/invoices/${encodeURIComponent(id)}/approve`,
    SEND_EMAIL: (id: string) => `${API_BASE_URL}/api/invoices/${id}/send-email`,
    UPDATE: (id: string) => `${API_BASE_URL}/api/invoices/${encodeURIComponent(id)}`,
    DELETE: (id: string) => `${API_BASE_URL}/api/invoices/${encodeURIComponent(id)}`,
    AUTO_INVOICE_RUN: (date?: string) => `${API_BASE_URL}/api/invoices/auto-invoice/run${date ? `?date=${encodeURIComponent(date)}` : ''}`,
  },
  NETSUITE: {
    HEALTH: `${API_BASE_URL}/api/netsuite/health`,
    GOVERNANCE_LIMITS: `${API_BASE_URL}/api/netsuite/governance-limits`,
    CUSTOMERS: `${API_BASE_URL}/api/netsuite/customers`,
    CUSTOMERS_SEARCH: `${API_BASE_URL}/api/netsuite/customers/search`,
    CUSTOMER_BY_ID: (id: string) => `${API_BASE_URL}/api/netsuite/customers/${id}`,
    CUSTOMER_ADDRESS: (id: string) => `${API_BASE_URL}/api/netsuite/customers/${id}/address`,
    CUSTOMERS_ALL: `${API_BASE_URL}/api/netsuite/customers/all`,
    INVOICES: `${API_BASE_URL}/api/netsuite/invoices`,
    INVOICE_BY_ID: (id: string) => `${API_BASE_URL}/api/netsuite/invoices/${id}`,
    INVOICES_BY_CUSTOMER: (customerId: string) => `${API_BASE_URL}/api/netsuite/invoices?customerId=${customerId}`,
    ITEMS_MVR: `${API_BASE_URL}/api/netsuite/items/autoaccess/mvr`,
    ITEMS_NMVTIS: `${API_BASE_URL}/api/netsuite/items/autoaccess/nmvtis`,
    ITEMS_BY_STATE: (code: string) => `${API_BASE_URL}/api/netsuite/items/autoaccess/state/${code}`,
    INQUIRY_FEES: `${API_BASE_URL}/api/netsuite/catalog/inquiry-fees`,
    BILLABLE_ITEMS: `${API_BASE_URL}/api/netsuite/catalog/billable-items`,
    DEPARTMENTS: `${API_BASE_URL}/api/netsuite/catalog/departments`,
    LOCATIONS: `${API_BASE_URL}/api/netsuite/catalog/locations`,
    CUSTOMER_AAS_PRICE: (nsCustomerId: string, serviceType: string, state?: string) =>
      `${API_BASE_URL}/api/netsuite/customer/${encodeURIComponent(nsCustomerId)}/aas-price?serviceType=${encodeURIComponent(serviceType)}${state ? `&state=${encodeURIComponent(state)}` : ''}`,
  },
  INVOICE_RECIPIENTS: {
    LIST: (clientId: string) => `${API_BASE_URL}/api/clients/${clientId}/invoice-recipients`,
    ADD: (clientId: string) => `${API_BASE_URL}/api/clients/${clientId}/invoice-recipients`,
    REMOVE: (clientId: string, recipientId: string) =>
      `${API_BASE_URL}/api/clients/${clientId}/invoice-recipients/${recipientId}`,
  },
  API_BASE_URL,
} as const;

// API Configuration
export const API_CONFIG = {
  TIMEOUT: 30000, // 30 seconds
  RETRY_COUNT: 3,
  RETRY_DELAY: 1000, // 1 second
} as const;
